import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { CheckOutlined, DownloadOutlined, LinkOutlined } from "@ant-design/icons";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { App, Dropdown } from "antd5";
import { ItemType } from "antd5/es/menu/interface";
import { useLocation } from "wouter";

import { OpportunityStageDropdownSelect } from "components/opportunities/opportunity_stage/OpportunityStageDropdownSelect";
import { StageOption } from "components/opportunities/opportunity_stage/utils";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import Tooltip from "components/ui/tooltip/Tooltip";
import { copyToClipboard } from "lib/clipboard";
import { BidInfo } from "lib/hooks/api/bid/bids/useBid";
import { useExportBidReportAsPDF } from "lib/hooks/api/bid/bids/useExportBidReportAsPDF";
import { useUpdateBidPublicity } from "lib/hooks/api/bid/bids/useUpdateBidPublicity";
import { BidNoBidResponse } from "lib/hooks/api/bid/scorecard/useGetBidReport";
import { useUpdateOpportunity } from "lib/hooks/api/opportunities/useUpdateOpportunity";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { downloadFileFromLink, sanitizeFilename } from "lib/utils";
import { exportDataToCsv } from "../../../lib/exportArrayToCsv";
import { Flex, Text } from "../../../styles/utility-components";
import NoBidModal from "./NoBidModal";
import { DeleteBidModal, RegenerateScorecardModal } from "./ReportModals";
import ToBidModal from "./ToBidModal";

/**
 * Generates a partial SHA-256 hash (8 char length) from a random 32-byte array.
 * @returns
 */
async function generateRandomSHA256(): Promise<string> {
  // Generate a random 32-byte array (256 bits)
  const randomValues = new Uint8Array(32);
  window.crypto.getRandomValues(randomValues);

  // Hash the random data with SHA-256
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", randomValues);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");

  return hash.slice(0, 8);
}

type Props = {
  bidId: string;
  bidInfo: BidInfo;
  data: BidNoBidResponse | undefined;
  urlShareSignature: string | null;
  slideoutOpen: boolean;
  setSlideoutOpen: (open: boolean) => void;
  onRegenerateBidReport: () => void;
};
export function BidNoBidHeaderActionButtons({
  bidId,
  bidInfo,
  data,
  urlShareSignature,
  slideoutOpen,
  setSlideoutOpen,
  onRegenerateBidReport,
}: Props) {
  const { teamId, notice, opportunityId, opportunity } = bidInfo;

  const { control, getValues, setValue } = useForm<{ opportunityId?: string; stageId: string }>({
    defaultValues: {
      opportunityId: opportunityId ?? undefined,
      stageId: opportunity?.stage?.id ?? "",
    },
  });

  const [joinedBidWaitlist, setJoinedBidWaitlist] = useLocalStorage<boolean>(
    "JOINED_BID_WAITLIST",
    false,
  ); // For growth/expert only

  const [shouldRedirectToRoot, setShouldRedirectToRoot] = useState(false);

  const { mutate: updateOpportunity } = useUpdateOpportunity(
    {
      onSuccess: (data) => {
        // Only update the form if the update is successful
        setValue("stageId", data.updateOpportunity.stage.id);
        message.success("Opportunity updated successfully");
        if (shouldRedirectToRoot) {
          setLocation("/");
        }
      },
      onError: (error) => {
        console.warn("Failed to update opportunity", error);
        message.error("Failed to update opportunity");
        setShouldRedirectToRoot(false);
      },
    },
    {
      startStageId: opportunity?.stage?.id ?? "",
      name: opportunity?.name ?? "",
    },
  );

  const [, setLocation] = useLocation();
  const { logEvent } = useTracking();

  const { message } = App.useApp();

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isLinkCopied, setisLinkCopied] = useState(false);

  const [isBidModalOpen, setBidModalOpen] = useState(false);
  const [isNoBidModalOpen, setNoBidModalOpen] = useState(false);

  // Temporary holding state for bid & no bid flows when opp stage is changed
  const [nextStage, setNextStage] = useState<{ stageId: string; stageName: string } | null>(null);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isRegenerateModalOpen, setRegenerateModalOpen] = useState(false);

  const { mutate: updateBidPublicity } = useUpdateBidPublicity();

  const { mutate: exportBidReportAsPDF } = useExportBidReportAsPDF({
    onSuccess: (data) => {
      if (data?.exportBidReportAsPDF?.url) {
        const fileName = `Bid_no_bid_report${"_" + sanitizeFilename(opportunity?.name || "")}.pdf`;
        downloadFileFromLink(data.exportBidReportAsPDF.url, fileName);
      } else {
        message.error("Failed to export bid report as PDF");
      }
    },
    onError: (e) => {
      Sentry.captureException(e);
      message.error("Failed to export bid report as PDF");
    },
  });

  const hasEditAccess = !!window.currentUser && window.currentUser?.team.id === teamId;

  const handleCSVExport = useCallback(() => {
    if (data) {
      const questionsAndAnswers = data.flatMap((item) =>
        item.questions.map((question) => ({
          question: question.title,
          answer: question.answer?.answer || null,
        })),
      );
      const csvData = [
        { question: "Scorecard link", answer: window.location.href },
        { question: "Question", answer: "Answer" },
        ...questionsAndAnswers,
      ];

      const sanitisedFilename = `Bid_no_bid_report${"_" + sanitizeFilename(opportunity?.name || "")}.csv`;
      exportDataToCsv(sanitisedFilename, csvData, false);

      logEvent(EventNames.exportedData, {
        "Record GUID": notice?.guid,
        "Opportunity ID": opportunityId,
        "Bid ID": bidId,
        "Action type": "Button clicked",
        "Data type": "Bid qualification scorecard (csv)",
      });
    }
  }, [data, logEvent, notice?.guid, opportunity?.name, opportunityId, bidId]);

  const handlePDFExport = () => {
    exportBidReportAsPDF({ input: { id: bidId } });
    logEvent(EventNames.exportedData, {
      "Record GUID": notice?.guid,
      "Opportunity ID": opportunityId,
      "Bid ID": bidId,
      "Action type": "Button clicked",
      "Data type": "Bid qualification (pdf)",
    });
  };

  async function copySignedUrl() {
    let shareUrl = window.location.href;

    // TODO: Generate signature in the backend - assuming we are going to carry
    // on with this method of locking bids down
    // Generate the hash or get it from the URL or DB
    const signature = urlShareSignature ?? (await generateRandomSHA256());

    if (!shareUrl.includes(signature)) {
      // Append the hash as a query parameter (e.g., ?s=...), if it doesn't
      // exist in the url already
      shareUrl = `${shareUrl}${shareUrl.includes("?") ? "&" : "?"}sig=${signature}`;
    }

    const clipboardContent = {
      plain: shareUrl,
    };

    const copiedToClipboard = await copyToClipboard(clipboardContent);

    logEvent(EventNames.qualReportLinkCopied, {
      "Action type": "Link clicked",
    });

    if (!copiedToClipboard) {
      void message.error("Failed to copy link to clipboard", 2);
      return;
    }

    setisLinkCopied(true);

    // Only populate db with signature if it doesn't exist/hasn't happened already
    if (!urlShareSignature) {
      updateBidPublicity({ input: { bidId, publicHash: signature } });
    }
  }

  const updateOpportunityStage = (stageId: string) => {
    if (!opportunityId) {
      return;
    }
    updateOpportunity({
      opportunity: {
        id: opportunityId,
        stageId,
      },
    });
  };

  const handleOpportunityStageChange = (stageId: string, option?: StageOption) => {
    switch (option?.transitionGroup) {
      case "Bidding": {
        if (joinedBidWaitlist) {
          const previousStageId = getValues("stageId");
          if (stageId !== previousStageId) {
            updateOpportunityStage(stageId);
          }
        } else {
          setNextStage({ stageId, stageName: option.label });
          setBidModalOpen(true);
        }
        break;
      }

      case "Closed": {
        setNextStage({ stageId, stageName: option.label });
        setNoBidModalOpen(true);
        break;
      }

      default: {
        const previousStageId = getValues("stageId");
        if (stageId !== previousStageId) {
          updateOpportunityStage(stageId);
        }
      }
    }
  };

  const shareMenuItems: ItemType[] = [
    {
      key: "shareLink",
      label: isLinkCopied ? (
        <MenuItem primaryTextColor>
          <CheckOutlined />
          Link copied
        </MenuItem>
      ) : (
        <MenuItem onClick={async () => copySignedUrl()}>
          <LinkOutlined />
          Copy shareable link
        </MenuItem>
      ),
    },
    {
      key: "download-csv",
      label: (
        <MenuItem onClick={handleCSVExport}>
          <DownloadOutlined />
          Download CSV
        </MenuItem>
      ),
      disabled: !data || data.length == 0,
    },
    {
      key: "download-pdf",
      label: (
        <MenuItem onClick={handlePDFExport}>
          <UIcon icon="fileDownload02" />
          Download PDF
        </MenuItem>
      ),
      disabled: !data || data.length == 0,
    },
  ];

  return (
    <>
      <ActionButtonContainer readOnlyUser={!hasEditAccess}>
        {!hasEditAccess && (
          <Tooltip variant="dark" content="Log in to collaborate" side="bottom">
            <ViewOnlyText>View-only</ViewOnlyText>
          </Tooltip>
        )}
        <Tooltip content={slideoutOpen ? "Hide documents" : "View documents"} side="bottom">
          <Button
            as="button"
            onClick={() => {
              if (!slideoutOpen) {
                logEvent(EventNames.tenderDocumentsTrayOpened, {
                  "Opportunity ID": opportunityId,
                  "Action type": "Button clicked",
                });
              }
              setSlideoutOpen(!slideoutOpen);
            }}
            icon="folder"
            buttonStyle="inverse"
            iconProps={{ color: "sysPrimaryDefault" }}
          />
        </Tooltip>

        <Dropdown
          menu={{ items: shareMenuItems }}
          overlayStyle={{ minWidth: "170px" }}
          open={isDropdownOpen}
          disabled={!data || data.length == 0}
          mouseLeaveDelay={0.5}
          onOpenChange={(open) => {
            if (!open) {
              setTimeout(() => {
                setDropdownOpen(false);
                setisLinkCopied(false); // Reset state when closing
              }, 500); // Delay for "Link copied" message visibility
            } else {
              setDropdownOpen(open);
            }
          }}
        >
          <Button buttonType="secondary" disabled={!data || data.length == 0} icon="share07">
            <Text fontWeight="500">Share</Text>
            <UIcon icon="chevronDown" />
          </Button>
        </Dropdown>
        {hasEditAccess && (
          <>
            {opportunityId && (
              <form>
                <ClassNames>
                  {({ css }) => (
                    <OpportunityStageDropdownSelect
                      name="stageId"
                      control={control}
                      label=""
                      variant="borderless"
                      dropdownStyle={{ width: "300px" }}
                      fillbackground
                      className={css({ maxWidth: 250, borderRadius: 4 })}
                      onChange={handleOpportunityStageChange}
                      preventFormValueChange
                    />
                  )}
                </ClassNames>
              </form>
            )}

            <DropdownMenu
              trigger={
                <Button
                  buttonType="tertiary"
                  icon="dotsVertical"
                  iconProps={{ color: "sysTextDefault" }}
                />
              }
              width={175}
            >
              <DropdownMenuItem
                key="regenerate"
                icon="refreshCw03"
                onClick={() => setRegenerateModalOpen(true)}
              >
                Regenerate
              </DropdownMenuItem>
              <DropdownMenuItem
                key="manageCriteria"
                icon="settings01"
                onClick={() => {
                  setLocation(
                    opportunityId
                      ? `/${opportunityId}/bid_qualification/${bidId}/bid-self-serve`
                      : `/${bidId}/bid-self-serve`,
                  );
                }}
              >
                Manage criteria
              </DropdownMenuItem>
              <DropdownMenuItem
                key="delete"
                icon="trash03"
                iconProps={{ color: "sysDestructiveDefault" }}
                onClick={() => setDeleteModalOpen(true)}
              >
                <Text color="sysDestructiveDefault">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenu>

            <DeleteBidModal
              bidId={bidId}
              opportunityId={opportunityId ?? ""}
              isOpen={isDeleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
            />
            <RegenerateScorecardModal
              isOpen={isRegenerateModalOpen}
              onRegenerate={onRegenerateBidReport}
              onClose={() => setRegenerateModalOpen(false)}
              modalText="You're about to regenerate all AI responses in this report. Please note, this action cannot be undone."
            />
          </>
        )}
      </ActionButtonContainer>

      {nextStage && opportunityId && (
        <>
          <ToBidModal
            open={isBidModalOpen}
            onOpenChange={(open) => setBidModalOpen(open)}
            stageName={nextStage.stageName.toLocaleLowerCase()}
            setJoinedBidWaitlist={() => setJoinedBidWaitlist(true)}
            updateStage={() => updateOpportunityStage(nextStage.stageId)}
          />

          <NoBidModal
            open={isNoBidModalOpen}
            onOpenChange={(open) => setNoBidModalOpen(open)}
            opportunityId={opportunityId}
            stageName={nextStage.stageName.toLocaleLowerCase()}
            onConfirm={() => {
              updateOpportunityStage(nextStage.stageId);
              // Redirect to the opportunities list page after update of opportunity
              setShouldRedirectToRoot(true);
            }}
          />
        </>
      )}
    </>
  );
}

const MenuItem = styled(Flex)<{ primaryTextColor?: boolean }>(({ theme, primaryTextColor }) => ({
  alignItems: "center",
  gap: "8px",
  color: primaryTextColor ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
}));

const ActionButtonContainer = styled(Flex)<{ readOnlyUser: boolean }>(({ readOnlyUser }) => ({
  alignItems: "center",
  gap: 8,
  // Fixing alignment of buttons depending on one of 3 outcomes.
  paddingRight: !readOnlyUser ? 0 : window.currentUser ? 24 : 8,
}));

const ViewOnlyText = styled(Text)(({ theme }) => ({
  cursor: "pointer",
  color: theme.colors.sysPrimaryDefault,
  paddingRight: 24,
}));
